import { Component, EventEmitter, Output, HostListener, ElementRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, merge, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { routeTitleMap } from '../../utils/constants';
import { AuthStore } from '../../../core/services/auth-store';
import { Header as HeaderService } from '../../../core/services/header';
import { HeaderDropdownOption } from '../../utils/types';

@Component({
  selector: 'app-header',
  templateUrl: './header.html',
  styleUrls: ['./header.css'],
  imports: [CommonModule, TranslatePipe]
})
export class Header {
  @Output() toggleSidebar = new EventEmitter<void>();

  headerTitle = '';
  dropdownOpen = false;
  showNotifications = true;
  showRightSection = true;
  dropdownOptions: HeaderDropdownOption[] = [];

  user = { name: '', initials: '', profileImage: '' };
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private authStore: AuthStore,
    private translate: TranslateService,
    private headerService: HeaderService,
    private elRef: ElementRef
  ) { }

  ngOnInit() {
    merge(
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)),
      this.translate.onLangChange,
      this.headerService.config$
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateHeader());

    this.loadUserInfo();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateHeader() {
    const config = this.headerService.currentConfig;

    // Priority to custom title if passed
    if (config.title) this.headerTitle = config.title;
    else this.setHeaderTitle();

    this.showNotifications = config.showNotifications ?? true;
    this.showRightSection = config.showRightSection ?? true;
    this.dropdownOptions = config.dropdownOptions || [];
  }

  private setHeaderTitle() {
    const currentUrl = this.router.url;
    const matchedKey = Object.keys(routeTitleMap).find((key) =>
      currentUrl.includes(key)
    );

    if (matchedKey) {
      const translationKey = routeTitleMap[matchedKey];
      this.translate.get(translationKey).subscribe((translatedTitle) => {
        this.headerTitle = translatedTitle;
      });
    } else {
      this.headerTitle = '';
    }
  }

  private loadUserInfo() {
    const userData = this.authStore.getUser();
    if (userData) {
      this.user.name = userData.name;
      this.user.profileImage = userData.profileImage;
      this.user.initials = userData.name
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase();
    }
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  handleOptionClick(option: HeaderDropdownOption) {
    this.dropdownOpen = false;

    if (option.action) option.action();
    else if (option.route) this.router.navigate([option.route]);
  }

  navToNotifications() {
    this.router.navigate(['/notifications']);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.dropdownOpen) return;

    const clickedInside = this.elRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.dropdownOpen = false;
    }
  }
  
  @HostListener('document:keydown.escape')
  onEscape() {
    this.dropdownOpen = false;
  }
}
